/* ============================================================================
   ltt-social — the reels and Instagram rails.

   Two jobs, one file, because both rails hit the same browser blind spot.

   ---------------------------------------------------------------------------
   1. LAZY LOADING (both sections)

   `loading="lazy"` DOES NOT WORK inside these rails, and this is not a
   preference — it was measured on 2026-08-10. Native lazy loading decides what
   to fetch from an element's position, recalculating on scroll and layout. A
   marquee moves by a CSS transform on the compositor, which notifies neither.
   Tiles that started off-screen were never fetched and slid into view as grey
   boxes. (The same blind spot kept IntersectionObserver-per-video from ever
   firing, which is why the old autoplay build played nothing.)

   So laziness is driven from the SECTION, which really is moved by scrolling:
   an observer with a 400px margin swaps every data-src to src just before the
   section arrives. Until then the section costs ZERO bytes of media. Both
   passes of the marquee share one set of URLs, so a 4-reel rail fetches 4
   posters, not 8.

   No-JS fallback: nothing loads. Acceptable here — these are two decorative
   social sections, and the alternative (src + loading="lazy") is the broken
   behaviour above.

   ---------------------------------------------------------------------------
   2. PLAY ONE REEL AT A TIME (reels only)

   Hamdi, 2026-08-10: every reel stands still with a play button; the rail
   keeps sliding; a tap plays that one reel and nothing else.

   The clips are staying full length (42-66s, ~15MB each at 1080p), so this is
   also the performance design, not just the interaction one — preload="none"
   means a reel costs nothing until someone asks for it.

   Playing stops the rail. Reading a video that is sliding out of frame is not
   possible, so the marquee pauses for as long as something is playing, and
   resumes when it ends or leaves the screen. Sound is ON: the visitor pressed
   play on a creator talking, and autoplay's muted requirement does not apply
   to a gesture-initiated play.
   ========================================================================= */
(function () {
  'use strict';

  /* --- lazy media ------------------------------------------------------- */

  function reveal(section) {
    section.querySelectorAll('img[data-src]').forEach(function (img) {
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      img.src = img.dataset.src;
      delete img.dataset.src;
      delete img.dataset.srcset;
    });
  }

  function lazySection(section) {
    if (!('IntersectionObserver' in window)) {
      reveal(section);
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        if (!entries[0].isIntersecting) return;
        reveal(section);
        io.disconnect();
      },
      // Start fetching before the section is on screen, so the posters are
      // decoded by the time it arrives rather than popping in under the reader.
      { rootMargin: '400px 0px' }
    );
    io.observe(section);
  }

  /* --- reels: one at a time --------------------------------------------- */

  function reels(section) {
    var marquee = section.querySelector('.ltt-marquee');
    var current = null;

    function stop(video) {
      if (!video) return;
      video.pause();
      video.currentTime = 0;
      video.removeAttribute('controls');
      video.closest('.ltt-reels__screen').classList.remove('is-playing');
    }

    function release() {
      if (marquee) marquee.classList.remove('is-paused');
    }

    section.querySelectorAll('.ltt-reels__screen').forEach(function (screen) {
      var video = screen.querySelector('video');
      var button = screen.querySelector('.ltt-reels__play');
      if (!video || !button) return;

      button.addEventListener('click', function () {
        // One at a time: whatever was playing goes back to its poster.
        if (current && current !== video) stop(current);

        current = video;
        screen.classList.add('is-playing');
        // controls only once playing — a permanent control bar over a stopped
        // poster competes with the play button for the same tap.
        video.setAttribute('controls', '');
        video.muted = false;
        var p = video.play();
        if (p && p.catch)
          p.catch(function () {
            // Blocked (backgrounded tab, decoder busy): put the tile back
            // rather than leaving a controls bar over a frozen frame.
            stop(video);
            release();
          });
        if (marquee) marquee.classList.add('is-paused');
      });

      // Ended: the reel is over, so restore the tile and let the rail run.
      video.addEventListener('ended', function () {
        stop(video);
        if (current === video) current = null;
        release();
      });

      // Paused by hand: leave the frame up and the rail still — the reader is
      // most likely about to resume, and sliding it away would be hostile.
      video.addEventListener('play', function () {
        if (marquee) marquee.classList.add('is-paused');
      });
    });

    // Scrolled away from: stop the download and give the rail back. Watching
    // the SECTION is safe — unlike the reels themselves, it only moves when
    // the page scrolls.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          if (entries[0].isIntersecting) return;
          stop(current);
          current = null;
          release();
        },
        { threshold: 0 }
      ).observe(section);
    }

    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) return;
      stop(current);
      current = null;
      release();
    });
  }

  /* --- wiring ------------------------------------------------------------ */

  function bind(root) {
    (root || document).querySelectorAll('.ltt-reels, .ltt-ig').forEach(function (section) {
      if (section.dataset.lttSocialBound) return;
      section.dataset.lttSocialBound = '1';
      lazySection(section);
      if (section.classList.contains('ltt-reels')) reels(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      bind(document);
    });
  } else {
    bind(document);
  }

  // Theme editor: a re-rendered section brings brand new nodes with it.
  document.addEventListener('shopify:section:load', function (e) {
    bind(e.target);
  });
})();
